import { api } from '../client';

export interface DashboardMetrics {
  totalItems: number;
  totalLocations: number;
  totalSuppliers: number;
  totalPurchaseOrders: number;
  totalShipments: number;
  activeAlerts: number;
  pendingApprovals: number;
  totalInventoryValue: number;
}

export interface DashboardDistributions {
  purchaseOrders: Record<string, number>;
  shipments: Record<string, number>;
  inventory: {
    optimal: number;
    low: number;
    stockout: number;
    overstock: number;
    total: number;
  };
}

export interface DashboardData {
  metrics: DashboardMetrics;
  distributions: DashboardDistributions;
  recentActivity: {
    purchaseOrders: Array<{
      id: string;
      poNumber: string;
      status: string;
      totalAmount?: number;
      supplier?: { name: string };
      createdAt: string;
    }>;
    shipments: Array<{
      id: string;
      shipmentNumber: string;
      status: string;
      origin?: { name: string };
      destination?: { name: string };
      createdAt: string;
    }>;
    alerts: Array<{
      id: string;
      type: string;
      severity: string;
      title: string;
      createdAt: string;
    }>;
  };
}

export interface TrendData {
  date: string;
  purchaseOrders: number;
  poValue: number;
  shipments: number;
  alerts: number;
}

export interface Recommendation {
  id: string;
  type: string;
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  title: string;
  description: string;
  entityType: string;
  entityId: string;
  suggestedAction?: {
    action: string;
    quantity?: number;
  };
}

export const analyticsApi = {
  async getDashboard(companyId?: string) {
    const response = await api.get<DashboardData>('/api/analytics/dashboard', {
      params: companyId ? { companyId } : undefined,
    });
    return response.data;
  },

  async getStockDistribution(companyId?: string) {
    const response = await api.get<DashboardDistributions['inventory']>('/api/analytics/stock-distribution', {
      params: companyId ? { companyId } : undefined,
    });
    return response.data;
  },

  async getTrends(companyId?: string, days = 30) {
    const response = await api.get<TrendData[]>('/api/analytics/trends', {
      params: { companyId, days },
    });
    return response.data;
  },

  async getRecommendations(companyId?: string) {
    const response = await api.get<Recommendation[]>('/api/analytics/recommendations', {
      params: companyId ? { companyId } : undefined,
    });
    return response.data;
  },

  async markRecommendationActioned(id: string) {
    const response = await api.post(`/api/analytics/recommendations/${id}/action`);
    return response.data;
  },
};
